import { z } from 'zod';
import type { ToolReadingDto } from './gaps';
import { HORIZON_KEYS, type HorizonInitiative, type HorizonKey, type Horizontes } from './strategy';

/* ─────────────────────────────────────────────────────────────────────
   3 Horizontes de Inovação — as iniciativas, as contas e as leituras.

   Cada horizonte tem iniciativas com nome e descrição (e evidências). O
   gráfico "Leitura visual dos horizontes" põe cada iniciativa num plano:
   mercados (atuais → novos) × produtos e ativos (atuais → novos). A IA
   lê o portfólio e diz onde cada uma cai, os critérios e o alinhamento;
   enquanto ela não leu, o ponto fica em volta do centro do horizonte.
   ───────────────────────────────────────────────────────────────────── */

export const HORIZON_META: Record<HorizonKey, {
  code: 'H1' | 'H2' | 'H3'; label: string; sub: string; bullets: [string, string];
  quick: string; outcome: string; center: { mercado: number; ativos: number };
}> = {
  h1: {
    code: 'H1', label: 'Essencial', sub: 'Core Business', bullets: ['Demandas existentes / mercados já servidos', 'Capacidades existentes e em uso'],
    quick: 'Fortalece a base', outcome: 'Otimiza o presente', center: { mercado: 20, ativos: 22 },
  },
  h2: {
    code: 'H2', label: 'Adjacente', sub: 'Novas frentes próximas', bullets: ['Demandas existentes ainda não endereçadas', 'Capacidades existentes ainda não utilizadas'],
    quick: 'Expande adjacências', outcome: 'Amplia oportunidades', center: { mercado: 50, ativos: 50 },
  },
  h3: {
    code: 'H3', label: 'Transformacional', sub: 'O futuro do negócio', bullets: ['Novas demandas / mercados emergentes', 'Novas capacidades'],
    quick: 'Explora futuros', outcome: 'Cria novos mercados', center: { mercado: 80, ativos: 78 },
  },
};

/** "Como ler os cenários" */
export const HORIZON_LENSES = [
  { key: 'mercado', label: 'Mercado atual', desc: 'Demandas de hoje, nos mercados que já atendemos.' },
  { key: 'capacidades', label: 'Capacidades atuais', desc: 'Recursos, competências e ativos que já possuímos.' },
  { key: 'ambicao', label: 'Ambição futura', desc: 'Novas oportunidades, novos mercados e maior impacto.' },
] as const;

export const HORIZON_CRITERIA = [
  ['aderencia', 'Aderência ao propósito'], ['viabilidade', 'Viabilidade com capacidades atuais'], ['potencial', 'Potencial de crescimento'],
] as const;

/** Ids estáveis (hz-N), únicos nos três horizontes — as evidências se ligam a eles. */
export function withHorizonIds(c: Horizontes): Horizontes {
  const used = new Set<string>();
  let n = 0;
  const fix = (xs: HorizonInitiative[]) => xs.map(i => {
    let id = i.id && !used.has(i.id) ? i.id : '';
    while (!id || used.has(id)) id = `hz-${++n}`;
    used.add(id);
    return { ...i, id };
  });
  return { h1: { initiatives: fix(c.h1.initiatives) }, h2: { initiatives: fix(c.h2.initiatives) }, h3: { initiatives: fix(c.h3.initiatives) } };
}

/** As iniciativas com nome, cada uma com o seu horizonte. */
export const horizonInitiatives = (c: Horizontes) =>
  HORIZON_KEYS.flatMap(h => c[h].initiatives.filter(i => i.name.trim()).map(i => ({ ...i, h })));

export function horizonMetrics(c: Horizontes) {
  const all = horizonInitiatives(c);
  const counts = Object.fromEntries(HORIZON_KEYS.map(h => [h, all.filter(i => i.h === h).length])) as Record<HorizonKey, number>;
  return { total: all.length, counts, described: all.filter(i => i.description.trim()).length, covered: HORIZON_KEYS.filter(h => counts[h] > 0).length };
}

/** Há o que ler: duas iniciativas com nome. */
export const horizontesReady = (c: Horizontes) => horizonMetrics(c).total >= 2;

/* ── a leitura da IA ─────────────────────────────────────────────── */

const nota = z.number().int().min(0).max(100);
export const horizonReadingSchema = z.object({
  /** onde cada iniciativa cai: mercado (0 = atual, 100 = novo) × ativos (0 = atuais, 100 = novos) */
  posicoes: z.array(z.object({
    id: z.string().max(48),
    /** o horizonte em que a iniciativa estava quando foi lida (o serviço preenche) */
    h: z.enum(HORIZON_KEYS).optional(),
    mercado: nota,
    ativos: nota,
  })).max(36),
  /** iniciativas que parecem de outro horizonte */
  deslocadas: z.array(z.object({ id: z.string().max(48), horizonte: z.enum(HORIZON_KEYS), motivo: z.string().trim().max(200).default('') })).max(4).default([]),
  criterios: z.object({ aderencia: nota, viabilidade: nota, potencial: nota }),
  alinhamento: nota,
  /** uma ou duas frases sobre o equilíbrio do portfólio */
  leitura: z.string().trim().min(12).max(320),
});
export type HorizonReading = z.infer<typeof horizonReadingSchema>;
export type HorizonReadingDto = ToolReadingDto<HorizonReading>;

/** O ponto de uma iniciativa no gráfico (0–100 nos dois eixos). */
export function horizonPoint(h: HorizonKey, index: number, id: string | undefined, reading: HorizonReading | null) {
  const p = id && reading?.posicoes.find(x => x.id === id && (!x.h || x.h === h));
  if (p) return { mercado: p.mercado, ativos: p.ativos, read: true };
  /* ainda não lida: uma vaga em volta do centro do horizonte (ângulo de ouro, sem sobrepor) */
  const c = HORIZON_META[h].center;
  const a = index * 2.4 + 0.8, r = index ? Math.min(11, 5 + 2.4 * Math.sqrt(index)) : 3;
  return { mercado: Math.round(c.mercado + r * Math.sin(a)), ativos: Math.round(c.ativos + r * Math.cos(a)), read: false };
}

/* ── a sugestão de iniciativa (a IA) ──────────────────────────────── */

export const horizonSuggestRequestSchema = z.object({
  horizon: z.enum(HORIZON_KEYS),
  /** a organização já deu o nome: a IA escreve a descrição */
  name: z.string().trim().max(120).optional(),
  /** sugestões já vistas e deixadas de lado (não repetir) */
  avoid: z.array(z.string().trim().max(120)).max(20).default([]),
});
export type HorizonSuggestRequest = z.infer<typeof horizonSuggestRequestSchema>;
export const horizonSuggestionSchema = z.object({
  name: z.string().trim().min(3).max(90),
  description: z.string().trim().min(12).max(420),
  /** de onde veio (ex.: "Macro Cenário · oportunidade tecnológica") */
  fonte: z.string().trim().max(90).default(''),
});
export type HorizonSuggestion = z.infer<typeof horizonSuggestionSchema>;
export interface HorizonSuggestionDto extends HorizonSuggestion { basic: boolean; note: string | null }
